import { useParams } from "@tanstack/react-router";
import { Grid, Modal } from "antd";
import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";
import { AgentCreateWizard } from "@/components/agents/agent-create-wizard";
import {
	type CliMode,
	CliSetupModal,
} from "@/components/agents/cli-setup-modal";
import { EnvironmentOnboardingWizard } from "@/components/environments/environment-onboarding-wizard";
import {
	JobCreateWizard,
	type JobWizardInitialValues,
} from "@/components/jobs/job-create-wizard";
import { ProjectCreateWizard } from "@/components/projects/project-create-wizard";
import { ReportCreateWizard } from "@/components/reports/report-create-wizard";
import {
	TaskCreateWizard,
	type TaskWizardInitialValues,
} from "@/components/tasks/task-create-wizard";
import { TemplateGalleryBrowser } from "@/components/template-gallery/template-gallery-browser";
import { WelcomeTour, welcomeTourKey } from "@/components/welcome-tour";
import { WelcomeWizard, welcomeWizardKey } from "@/components/welcome-wizard";
import { useFlag } from "@/contexts/flags-context";

interface WizardsContextType {
	/** Opens the task wizard, optionally pre-filled (e.g. duplicating a task). */
	openTaskCreateWizard: (initialValues?: TaskWizardInitialValues) => void;
	/**
	 * Opens the same wizard in template mode: it saves a reusable task template instead
	 * of creating a task. Pass a templateId to edit an existing template.
	 */
	openTaskTemplateWizard: (
		initialValues?: TaskWizardInitialValues,
		templateId?: string,
	) => void;
	/** Opens the scheduled job wizard, optionally pre-filled (e.g. from a task template). */
	openJobCreateWizard: (initialValues?: JobWizardInitialValues) => void;
	openProjectCreateWizard: () => void;
	openEnvironmentCreateWizard: () => void;
	/** Opens the gallery in multi-select mode, pre-filtered to and scoped to this environment —
	 * picked templates are created as workspace task templates bound to it. */
	openTemplateGalleryForEnvironment: (environment: {
		id: string;
		provider: string;
	}) => void;
	openAgentCreateWizard: () => void;
	openReportCreateWizard: () => void;
	openWelcomeWizard: () => void;
	openCliSetupWizard: (mode?: CliMode) => void;
	openWelcomeTour: () => void;
}

const WizardsContext = createContext<WizardsContextType | undefined>(undefined);

export function WizardsProvider({ children }: { children: ReactNode }) {
	const { workspaceId } = useParams({ strict: false }) as {
		workspaceId?: string;
	};
	const storageKey = welcomeWizardKey(workspaceId);
	const tourKey = welcomeTourKey(workspaceId);
	// Automated sessions turn the first-run popups off; opening them by hand still works.
	const onboardingDisabled = useFlag("ONBOARDING_DISABLED");
	const screens = Grid.useBreakpoint();
	// The tour points at the side nav, which is hidden on mobile — skip it there.
	const isMobile = !screens.md;
	const [taskOpen, setTaskOpen] = useState(false);
	const [taskInitialValues, setTaskInitialValues] =
		useState<TaskWizardInitialValues>();
	const [taskWizardKind, setTaskWizardKind] = useState<"task" | "template">(
		"task",
	);
	const [editingTemplateId, setEditingTemplateId] = useState<string>();
	const [jobOpen, setJobOpen] = useState(false);
	const [jobInitialValues, setJobInitialValues] =
		useState<JobWizardInitialValues>();
	const [projectOpen, setProjectOpen] = useState(false);
	const [environmentOpen, setEnvironmentOpen] = useState(false);
	const [agentOpen, setAgentOpen] = useState(false);
	const [reportOpen, setReportOpen] = useState(false);
	const [welcomeOpen, setWelcomeOpen] = useState(false);
	const [cliSetupOpen, setCliSetupOpen] = useState(false);
	const [cliSetupMode, setCliSetupMode] = useState<CliMode>("cli");
	const [tourOpen, setTourOpen] = useState(false);
	// Prompts the gallery right after a new environment is created, scoped to it.
	const [newEnvironment, setNewEnvironment] = useState<{
		id: string;
		provider: string;
	} | null>(null);
	const [galleryPickerOpen, setGalleryPickerOpen] = useState(false);

	// Auto-show for first-time users, scoped per workspace
	useEffect(() => {
		if (
			workspaceId &&
			!onboardingDisabled &&
			!localStorage.getItem(storageKey)
		) {
			setWelcomeOpen(true);
		}
	}, [workspaceId, onboardingDisabled, storageKey]);

	// After the welcome wizard is dismissed, run the nav tour once per workspace — only
	// inside a workspace, where the side nav it points at actually exists.
	useEffect(() => {
		if (
			workspaceId &&
			!onboardingDisabled &&
			!isMobile &&
			!welcomeOpen &&
			localStorage.getItem(storageKey) &&
			!localStorage.getItem(tourKey)
		) {
			setTourOpen(true);
		}
	}, [
		workspaceId,
		onboardingDisabled,
		isMobile,
		welcomeOpen,
		storageKey,
		tourKey,
	]);

	const openTemplateGalleryForEnvironment = (environment: {
		id: string;
		provider: string;
	}) => {
		setNewEnvironment(environment);
		setGalleryPickerOpen(true);
	};

	return (
		<WizardsContext.Provider
			value={{
				openTaskCreateWizard: (initialValues) => {
					setTaskInitialValues(initialValues);
					setTaskWizardKind("task");
					setEditingTemplateId(undefined);
					setTaskOpen(true);
				},
				openTaskTemplateWizard: (initialValues, templateId) => {
					setTaskInitialValues(initialValues);
					setTaskWizardKind("template");
					setEditingTemplateId(templateId);
					setTaskOpen(true);
				},
				openJobCreateWizard: (initialValues) => {
					setJobInitialValues(initialValues);
					setJobOpen(true);
				},
				openProjectCreateWizard: () => setProjectOpen(true),
				openEnvironmentCreateWizard: () => setEnvironmentOpen(true),
				openTemplateGalleryForEnvironment,
				openAgentCreateWizard: () => setAgentOpen(true),
				openReportCreateWizard: () => setReportOpen(true),
				openWelcomeWizard: () => setWelcomeOpen(true),
				openCliSetupWizard: (mode = "cli") => {
					setCliSetupMode(mode);
					setCliSetupOpen(true);
				},
				openWelcomeTour: () => setTourOpen(true),
			}}
		>
			{children}
			<TaskCreateWizard
				open={taskOpen}
				initialValues={taskInitialValues}
				kind={taskWizardKind}
				templateId={editingTemplateId}
				onClose={() => {
					setTaskOpen(false);
					setTaskInitialValues(undefined);
					setTaskWizardKind("task");
					setEditingTemplateId(undefined);
				}}
			/>
			<JobCreateWizard
				open={jobOpen}
				initialValues={jobInitialValues}
				onClose={() => {
					setJobOpen(false);
					setJobInitialValues(undefined);
				}}
			/>
			<ProjectCreateWizard
				open={projectOpen}
				onClose={() => setProjectOpen(false)}
			/>
			<EnvironmentOnboardingWizard
				open={environmentOpen}
				onClose={() => setEnvironmentOpen(false)}
				onCreated={(env) =>
					Modal.confirm({
						title: "Browse the template gallery?",
						content:
							"Add pre-made templates to this environment now, or skip and do it later from its page.",
						okText: "Browse gallery",
						cancelText: "Skip",
						onOk: () => openTemplateGalleryForEnvironment(env),
					})
				}
			/>
			{workspaceId && (
				<TemplateGalleryBrowser
					open={galleryPickerOpen}
					onClose={() => {
						setGalleryPickerOpen(false);
						setNewEnvironment(null);
					}}
					workspaceId={workspaceId}
					environmentId={newEnvironment?.id}
					defaultProviderFilter={newEnvironment?.provider}
				/>
			)}
			<AgentCreateWizard open={agentOpen} onClose={() => setAgentOpen(false)} />
			<ReportCreateWizard
				open={reportOpen}
				onClose={() => setReportOpen(false)}
			/>
			<WelcomeWizard
				open={welcomeOpen}
				onClose={() => setWelcomeOpen(false)}
				storageKey={storageKey}
			/>
			<CliSetupModal
				open={cliSetupOpen}
				onClose={() => setCliSetupOpen(false)}
				initialMode={cliSetupMode}
			/>
			<WelcomeTour
				open={tourOpen}
				storageKey={tourKey}
				onClose={() => setTourOpen(false)}
			/>
		</WizardsContext.Provider>
	);
}

export function useWizards() {
	const context = useContext(WizardsContext);
	if (context === undefined) {
		throw new Error("useWizards must be used within a WizardsProvider");
	}
	return context;
}
